/**
 * AppHeader — Platform module navigation bar.
 *
 * Rendered in the "aboveHeader" slot. On platform module routes (/, /qms,
 * /polyglot, /docs) it shows a module nav bar and uses Volto's BodyClass
 * helper to add body.app-mode, which hides the VLT CMS header via CSS.
 * On CMS content routes it renders nothing, letting the normal VLT header
 * show.
 *
 * Navigation tabs and auth controls are filtered by user role to avoid
 * exposing links to pages the user cannot access (TEST-PERM-001).
 *
 * See ARCH-2026-008 Phase A.
 */
import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BodyClass } from '@plone/volto/helpers';
import Cookies from 'universal-cookie';
import jwtDecode from 'jwt-decode';
import { useRole, clearRoleCache } from '../hooks/useRole';
import './AppHeader.css';

/** Routes that activate app mode (platform modules, not CMS content). */
const isAppRoute = (pathname) =>
  pathname === '/' ||
  pathname.startsWith('/qms') ||
  pathname.startsWith('/polyglot') ||
  pathname.startsWith('/docs') ||
  pathname.startsWith('/api-docs') ||
  pathname.startsWith('/documents') ||
  pathname.startsWith('/policies') ||
  pathname.startsWith('/login') ||
  pathname.startsWith('/logout');

const isAuthRoute = (pathname) =>
  pathname.startsWith('/login') || pathname.startsWith('/logout');

const allTabs = [
  { path: '/qms', label: 'QMS', roles: ['editor', 'admin'] },
  { path: '/polyglot', label: 'Polyglot', roles: ['public', 'user', 'contributor', 'editor', 'admin'] },
  { path: '/docs', label: 'Docs', roles: ['user', 'contributor', 'editor', 'admin'] },
  { path: '/policies', label: 'Policies', roles: ['public', 'user', 'contributor', 'editor', 'admin'] },
  { path: '/api-docs', label: 'API', roles: ['contributor', 'editor', 'admin'] },
  { path: '/en', label: 'Content', roles: ['contributor', 'editor', 'admin'] },
];

const AppHeader = () => {
  const { pathname } = useLocation();
  const { role, profile, loading } = useRole();
  const token = useSelector((state) => state.userSession?.token);
  const userMenuRef = useRef(null);

  const appMode = isAppRoute(pathname);
  const authRoute = isAuthRoute(pathname);
  const loggedIn = role !== 'public' && role !== 'loading';

  if (!appMode) return null;

  const visibleTabs = allTabs.filter((tab) => tab.roles.includes(role));

  // Display name: contributor profile first, then the JWT (nick's login/renew
  // tokens carry fullname; sub is the account id fallback).
  let displayName = profile?.fullname || '';
  if (!displayName && token) {
    try {
      const payload = jwtDecode(token);
      displayName = payload.fullname || payload.sub || '';
    } catch {
      displayName = '';
    }
  }

  const closeUserMenu = () => {
    if (userMenuRef.current) userMenuRef.current.open = false;
  };

  const handleLogout = (e) => {
    e.preventDefault();
    clearRoleCache();
    const cookies = new Cookies();
    cookies.remove('auth_token', { path: '/' });
    window.location.href = '/login';
  };

  return (
    <>
      <BodyClass className="app-mode" />
      <nav className="app-header">
        <Link to="/" className="app-header__brand">
          Ljusa Tider
        </Link>

        {!authRoute && (
          <div className="app-header__tabs">
            {visibleTabs.map((tab) => (
              <Link
                key={tab.path}
                to={tab.path}
                className={`app-header__tab${
                  tab.path !== '/en' && (pathname === tab.path || pathname.startsWith(tab.path + '/'))
                    ? ' app-header__tab--active'
                    : ''
                }`}
              >
                {tab.label}
              </Link>
            ))}
          </div>
        )}

        {!authRoute && (
          <div className="app-header__right">
            {loggedIn ? (
              <details className="app-header__user" ref={userMenuRef}>
                <summary className="app-header__auth app-header__user-trigger">
                  {displayName || 'Account'}
                </summary>
                <div className="app-header__user-menu" role="menu">
                  <Link
                    to="/change-password"
                    role="menuitem"
                    className="app-header__user-item"
                    onClick={closeUserMenu}
                  >
                    Change password
                  </Link>
                  <a
                    href="/logout"
                    role="menuitem"
                    className="app-header__user-item"
                    onClick={handleLogout}
                  >
                    Log out
                  </a>
                </div>
              </details>
            ) : (
              <Link to={`/login?return_url=${encodeURIComponent(pathname)}`} className="app-header__auth">
                Log in
              </Link>
            )}
          </div>
        )}
      </nav>
    </>
  );
};

export default AppHeader;
